import { registrationError } from '@/features/document-registration/presentation';
import { ApiError, apiErrorReason } from '@/lib/api';

import type { DocumentRequest, RequestKind, RequestStatus } from './types';

// Textos de las solicitudes de documento (G3-1, F1-23). Puro. Cero valores internos en
// pantalla (UX-COPY-01); los rechazos del alta reutilizan los textos del alta.

export interface Badge {
  label: string;
  cls: 'b-warn' | 'b-ok' | 'b-red' | 'b-mut';
}

const BADGES: Record<RequestStatus, Badge> = {
  pendiente: { label: 'Pendiente', cls: 'b-warn' },
  aprobada: { label: 'Aprobada', cls: 'b-ok' },
  rechazada: { label: 'Rechazada', cls: 'b-red' },
  cancelada: { label: 'Cancelada', cls: 'b-mut' },
};

export function statusBadge(status: RequestStatus): Badge {
  return BADGES[status];
}

export function kindLabel(kind: RequestKind): string {
  return kind === 'nuevo' ? 'Documento nuevo' : 'Modificación';
}

/** Filtro de estado de la bandeja; `all` = todas. */
export const STATUS_FILTERS: readonly { value: RequestStatus | 'all'; label: string }[] = [
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'aprobada', label: 'Aprobadas' },
  { value: 'rechazada', label: 'Rechazadas' },
  { value: 'cancelada', label: 'Canceladas' },
  { value: 'all', label: 'Todas' },
];

/** Bandeja vacía según el filtro de estado. */
export function emptyText(status: RequestStatus | 'all'): string {
  if (status === 'all') {
    return 'Todavía no hay solicitudes de documentos.';
  }
  const label = STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase() ?? '';
  return `No hay solicitudes ${label}.`;
}

const REASON_MIN = 3;
const REASON_MAX = 1000;

/** Motivo de la solicitud o del rechazo (misma regla que el API), o null si es válido. */
export function reasonError(raw: string): string | null {
  const reason = raw.trim();
  if (reason === '') {
    return 'Escribe el motivo';
  }
  if (reason.length < REASON_MIN) {
    return `Escribe al menos ${REASON_MIN} caracteres`;
  }
  return reason.length > REASON_MAX ? `El motivo admite hasta ${REASON_MAX} caracteres` : null;
}

const REJECTIONS: Record<string, string> = {
  request_pending: 'Ese documento ya tiene una solicitud de modificación pendiente.',
  not_vigente: 'Solo un documento vigente puede modificarse. Recarga la ficha.',
  request_resolved: 'Otra persona ya resolvió esta solicitud. Recarga la bandeja.',
  flow_already_active:
    'El documento ya tiene un flujo en curso: no se puede iniciar otra modificación.',
  no_base_version: 'El documento no tiene una versión vigente de la cual partir.',
  requires_lms_file:
    'El documento no tiene archivo en el LMS. Vincúlalo desde su ficha, en «Reserva y archivo», y vuelve a aprobar.',
  document_type_not_readable: 'No tienes acceso a ese tipo de documento. Elige otro.',
};

/** Mensaje accionable para un rechazo al crear, aprobar, rechazar o cancelar. */
export function requestError(err: unknown): string {
  const reason = apiErrorReason(err);
  const own = reason === undefined ? undefined : REJECTIONS[reason];
  if (own !== undefined) {
    return own;
  }
  if (err instanceof ApiError && err.status === 404) {
    return 'La solicitud ya no está disponible. Recarga la bandeja.';
  }
  return registrationError(err);
}

/** Aviso específico tras resolver o cancelar (UX-COPY-01). */
export function resolvedToast(r: DocumentRequest): string {
  if (r.status === 'aprobada') {
    return r.kind === 'nuevo'
      ? `Solicitud aprobada: código ${r.result?.code ?? ''} reservado`
      : `Modificación de ${r.target?.code ?? r.result?.code ?? ''} iniciada`;
  }
  return `Solicitud «${r.proposedTitle}» ${r.status === 'rechazada' ? 'rechazada' : 'cancelada'}`;
}
